import type { ReactNode } from 'react';
import { KakaoMapIcon } from '@/components/icons/KakaoMapIcon';
import { NaverMapIcon } from '@/components/icons/NaverMapIcon';

interface MapButtonsProps {
  kakaoMapUrl: string;
  naverMapUrl: string;
  studioTitle: string;
  aiIdPrefix?: string;
  className?: string;
}

export function MapButtons({ kakaoMapUrl, naverMapUrl, studioTitle, aiIdPrefix = 'studio-map-buttons', className = 'grid grid-cols-2 gap-2' }: MapButtonsProps) {
  return (
    <div className={className} data-ai-id={aiIdPrefix}>
      <MapAction
        href={kakaoMapUrl}
        label="카카오맵"
        unavailableLabel="카카오맵"
        ariaLabel={`${studioTitle} 카카오맵으로 열기`}
        icon={<KakaoMapIcon className="size-4" aiId={`${aiIdPrefix}-kakao-map-action-icon`} />}
        activeClassName="border-[#f9df32] bg-[#f9df32] text-slate-950"
        aiIdPrefix={`${aiIdPrefix}-kakao-map-action`}
      />
      <MapAction
        href={naverMapUrl}
        label="네이버맵"
        unavailableLabel="네이버맵"
        ariaLabel={`${studioTitle} 네이버맵으로 열기`}
        icon={<NaverMapIcon className="size-4" aiId={`${aiIdPrefix}-naver-map-action-icon`} />}
        activeClassName="border-[#03c75a] bg-[#03c75a] text-white"
        aiIdPrefix={`${aiIdPrefix}-naver-map-action`}
      />
    </div>
  );
}

interface MapActionProps {
  href: string;
  label: string;
  unavailableLabel: string;
  ariaLabel: string;
  icon: ReactNode;
  activeClassName: string;
  aiIdPrefix: string;
}

function MapAction({ href, label, unavailableLabel, ariaLabel, icon, activeClassName, aiIdPrefix }: MapActionProps) {
  const baseClassName =
    'inline-flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-lg border px-2.5 text-sm font-semibold transition focus:outline-none focus-visible:ring-4 focus-visible:ring-emerald-100';

  if (!href) {
    return (
      <button
        type="button"
        disabled
        className={`${baseClassName} cursor-not-allowed border-slate-200 bg-slate-50 text-slate-300`}
        title="관리자에서 지도 링크를 입력하면 사용할 수 있어요."
        aria-label={`${ariaLabel} 링크 미입력`}
        data-ai-id={`${aiIdPrefix}-disabled-button`}
      >
        <span className="inline-flex" data-ai-id={`${aiIdPrefix}-disabled-button-icon-wrapper`}>
          {icon}
        </span>
        <span data-ai-id={`${aiIdPrefix}-disabled-button-label-text`}>{unavailableLabel}</span>
      </button>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      aria-label={ariaLabel}
      className={`${baseClassName} ${activeClassName}`}
      data-ai-id={`${aiIdPrefix}-link`}
    >
      <span className="inline-flex" data-ai-id={`${aiIdPrefix}-link-icon-wrapper`}>
        {icon}
      </span>
      <span data-ai-id={`${aiIdPrefix}-link-label-text`}>{label}</span>
    </a>
  );
}
